import { useEffect, useState } from 'react'
import { AlertCircle, Clock, Mail, MoreVertical, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type {UserInviteResponse} from '@/lib/api/features/workspace-invites/types/UserInviteTypes';
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import {
  
  getInviteExpirationLabel,
  getInviteStatusColor,
  getInviteStatusLabel,
  isInviteActive
} from '@/lib/api/features/workspace-invites/types/UserInviteTypes'
import { getPendingInvites } from '@/lib/api/features/workspace-invites/api/getPendingInvites'
import { revokeInvite } from '@/lib/api/features/workspace-invites/api/revokeInvite'
import { resendInvite } from '@/lib/api/features/workspace-invites/api/resendInvite'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

interface InvitesListProps {
  workspaceSlug: string
  onInviteRevoked?: () => void
}

export function InvitesList({ workspaceSlug, onInviteRevoked }: InvitesListProps) {
  const { t } = useTranslation('settings')
  const { smartTime, dateTime: formatDateTime } = useDateTimeFormatters()
  const [invites, setInvites] = useState<Array<UserInviteResponse>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)

  const loadInvites = async () => {
    setIsLoading(true)
    try {
      const result = await getPendingInvites(workspaceSlug, {
        includeHistory: false, // Only show pending invites
      })
      setInvites(result.invites)
    } catch (error) {
      toast.error(t('members.invites.list.loadFailed'))
      logger.logError(
        error as Error,
        LogCategory.API_ERROR,
        {
          component: 'InvitesList',
          action: 'load_invites',
          workspaceSlug,
        }
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadInvites()
  }, [workspaceSlug])

  const handleResend = async (inviteId: string, email: string) => {
    setActionInProgress(inviteId)
    try {
      await resendInvite(workspaceSlug, inviteId)
      toast.success(t('members.invites.list.resentTo', { email }))
      await loadInvites()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('members.invites.list.resendFailed')
      toast.error(message)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleRevoke = async (inviteId: string, email: string) => {
    if (!confirm(t('members.invites.list.revokeConfirm', { email }))) {
      return
    }

    setActionInProgress(inviteId)
    try {
      await revokeInvite(workspaceSlug, inviteId)
      toast.success(t('members.invites.list.revokedFor', { email }))
      setInvites((prev) => prev.filter((i) => i.id !== inviteId))
      onInviteRevoked?.()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('members.invites.list.revokeFailed')
      toast.error(message)
    } finally {
      setActionInProgress(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
        ))}
      </div>
    )
  }

  if (invites.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Mail className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mt-4 text-sm font-medium">{t('members.invites.list.empty')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('members.invites.list.emptyDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('members.invites.list.pending', { count: invites.length })}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={loadInvites}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('members.invites.list.columns.email')}</TableHead>
              <TableHead>{t('members.invites.list.columns.role')}</TableHead>
              <TableHead>{t('members.invites.list.columns.status')}</TableHead>
              <TableHead>{t('members.invites.list.columns.expires')}</TableHead>
              <TableHead>{t('members.invites.list.columns.sent')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => {
              const isActive = isInviteActive(invite)
              const expirationLabel = getInviteExpirationLabel(invite)

              return (
                <TableRow key={invite.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{invite.email}</div>
                        {invite.customMessage && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {invite.customMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {invite.role || t('members.invites.list.defaultRole')}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={getInviteStatusColor(invite.status)}
                    >
                      {getInviteStatusLabel(invite.status)}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span
                        className={
                          !isActive ? 'text-destructive font-medium' : 'text-muted-foreground'
                        }
                      >
                        {expirationLabel}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    <time
                      title={formatDateTime(new Date(invite.createdOn), { dateStyle: 'medium', timeStyle: 'short' })}
                      dateTime={new Date(invite.createdOn).toISOString()}
                    >
                      {smartTime(new Date(invite.createdOn))}
                    </time>
                  </TableCell>

                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={actionInProgress === invite.id}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleResend(invite.id, invite.email)}
                          disabled={!isActive}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          {t('members.invites.list.actions.resend')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleRevoke(invite.id, invite.email)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('members.invites.list.actions.revoke')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {invites.some((i) => !isInviteActive(i)) && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-medium">{t('members.invites.list.expiredBanner')}</span>{' '}
            {t('members.invites.list.expiredBannerHelp')}
          </div>
        </div>
      )}
    </div>
  )
}
